import React from 'react'
import styled, { css, keyframes } from 'styled-components'
import getPalette, { IPalette } from '../../../helpers/colors/colors'
import minQueryHelper from '../../../helpers/minQueryHelper'
import { IGameAnswer, IGameAnswerMediaText, IGameAnswerMediaImage, AnswerType } from '../../../helpers/data/question'
import getFont from '../../../helpers/fonts/fonts'
import Asset from '../../../components/asset/Asset'
import { assetTypes, AssetType } from '../../../helpers/data/assetData'
import answerStates from '../../../helpers/enums/answerStates'
import checkIcon from '../../../assets/icons/checkmark.svg'
import { IAccessibilityStore } from '../../../redux/reducers/accessibilityReducer'
import RichTextContent from '../../../components/richText/RichTextContent'
import { ensurePlainText } from '../../../helpers/html/sanitizer'

const palette: IPalette = getPalette()

const VisuallyHiddenInput = styled.input`
	position: absolute;
	width: 1px;
	height: 1px;
	padding: 0;
	margin: -1px;
	overflow: hidden;
	clip: rect(0, 0, 0, 0);
	white-space: nowrap;
	border: 0;
`

const Wrapper = styled.label`
	display: block;
	width: 100%;
	height: 100%;
	background-color: ${palette.white};
	border: none;
	color: ${palette.grayBlue};
	cursor: pointer;
	font-family: ${getFont('secondaryRegular')};
	font-size: 24px;
	line-height: 24px;
	overflow: hidden;
	vertical-align: top;
	position: relative;
	transition: color 500ms ease-out;

	@media (max-width: ${minQueryHelper}px) {
		font-size: 14px;
	}

	&.unselected {
		color: ${palette.white};
	}

	&:has(input:focus-visible) {
		outline: 3px solid #666666;
		outline-offset: 2px;
		box-shadow: 0 0 0 1px rgba(102, 102, 102, 0.3);
	}
`

const AnswerColor = styled.div`
	width: 100%;
	height: 100%;
	background-color: rgba(255, 255, 255, 0);
	border: 2px solid ${palette.grayBlue};
	border-radius: 5px;
	overflow: hidden;
	position: absolute;
	top: 0;
	left: 0;
	transition: background-color 500ms ease-out, border 500ms ease-out, opacity 500ms ease-out;
	&.correct {
		background-color: ${palette.success};
		border: 2px solid ${palette.success};
		opacity: 0.5;
	}
	&.incorrect {
		background-color: ${palette.alert};
		border: 2px solid ${palette.alert};
		opacity: 0.5;
	}
	&.unselected {
		background-color: #b3d4fc;
		border: 2px solid #b3d4fc;
		opacity: 0.8;
	}
`

const AnswerMediaContainer = styled.div`
	width: 100%;
	height: 100%;
	display: flex;
	padding: 10px;
	overflow: auto;
	position: absolute;
	top: 0;
	left: 0;
`

const AnswerText = styled.div`
	display: block;
	font-size: 20px;
	margin: auto;
	white-space: pre-wrap;
`

const AnswerImage = styled.img`
	width: 100%;
	height: 100%;
	background-color: white;
	background-position: center;
	background-repeat: no-repeat;
	background-size: contain;
	padding: 10px;
	transition: opacity 0.5s ease-out;
	object-fit: contain;
`

const headerIn = keyframes`
	0% {
		transform: translateY(-100%);
	}
	100% {
		transform: translateY(0);
	}
`

const AnswerHeaderContainer = styled.div`
	width: 100%;
	height: 40px;
	animation: ${headerIn} 0.5s ease-out;
	animation-fill-mode: both;
	display: flex;
	position: absolute;
	top: 0;
	left: 0;
	&.correct {
		background-color: #5fd598;
	}
	&.incorrect {
		background-color: #f26c59;
	}
`

const AnswerHeaderText = styled.div`
	color: #ffffff;
	flex: 1 1 auto;
	font-size: 16px;
	line-height: 40px;
	padding-left: 10px;
	text-align: left;
`

const AnswerAnimation = styled.div`
	width: 40px;
	height: 40px;
	flex: 0 0 auto;
	overflow: hidden;
`

const AnswerCheckmark = styled('div')<{ isMultipleAnswer?: boolean; answerSkin?: any; answerState?: any }>`
	width: 26px;
	height: 26px;
	padding: 3px;
	position: absolute;
	top: 14px;
	right: 14px;
	border-radius: ${(props) => (props.isMultipleAnswer ? '5px' : '50%')};
	background-color: ${(props) =>
		props.isMultipleAnswer && props.answerState === answerStates.UNANSWERED ? 'white' : props.answerSkin.primary};

	${(props) =>
		props.isMultipleAnswer &&
		props.answerState === answerStates.UNANSWERED &&
		props.answerSkin &&
		css`
			border: 2px solid ${props.answerSkin.primary};
		`}
`

const AnswerCheckmarkIcon = styled.img`
	width: 100%;
	height: 100%;
`

export interface IAnswerSkin {
	primary: string
	secondary: string
}

export interface IAnswerProps {
	onSelect: (answerData: IGameAnswer) => void
	answerData: IGameAnswer
	answerState: string
	delay: number
	answerSkin: IAnswerSkin
	headerText: string
	animationPath: string
	isMultipleAnswer: boolean
	accessibility?: IAccessibilityStore
	// Hands the answer's screen-reader text up to Question, which needs it for the end-of-question
	// announcement and has no other way to reach the typeset formulas. See handleTextSpeechChange.
	onSpeechChange?: (answerUid: string, speech: string) => void
}

const ARROW_KEYS = {
	NEXT: ['ArrowDown', 'ArrowRight'],
	PREV: ['ArrowUp', 'ArrowLeft'],
}

interface IAnswerState {
	// MathJax's spoken form of a text answer, once typesetting settles. Empty until then, and for
	// answers with no math at all; buildAriaLabel falls back to plain text in both cases.
	textSpeech: string
}

class Answer extends React.PureComponent<IAnswerProps, IAnswerState> {
	state: IAnswerState = {
		textSpeech: '',
	}

	handleTextSpeechChange = (textSpeech: string) => {
		this.setState({ textSpeech })
		if (this.props.onSpeechChange) {
			this.props.onSpeechChange(this.props.answerData.uid, textSpeech)
		}
	}

	handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
	const isArrowKey = [...ARROW_KEYS.NEXT, ...ARROW_KEYS.PREV].includes(event.key)

	// For radio buttons: prevent auto-selection and implement manual navigation
	if (isArrowKey && !this.props.isMultipleAnswer) {
		event.preventDefault() // Prevent browser's auto-selection

		// Manual focus navigation for radio buttons
		const currentInput = event.currentTarget
		const container = currentInput.closest('[role="radiogroup"]')
		const allRadios = container
			? Array.from(container.querySelectorAll<HTMLInputElement>('input[type="radio"]'))
			: []

		if (allRadios.length > 0) {
			const currentIndex = allRadios.indexOf(currentInput)
			let nextIndex = currentIndex

			if (ARROW_KEYS.NEXT.includes(event.key)) {
				nextIndex = (currentIndex + 1) % allRadios.length
			} else if (ARROW_KEYS.PREV.includes(event.key)) {
				nextIndex = (currentIndex - 1 + allRadios.length) % allRadios.length
			}

			allRadios[nextIndex]?.focus()
		}
		return
	}

	// For checkboxes: prevent arrow navigation (use Tab instead)
	if (isArrowKey && this.props.isMultipleAnswer) {
		event.preventDefault()
		return
	}

	// Handle Space and Enter for selection (both radio and checkbox)
	if (['Enter', ' '].includes(event.key)) {
		event.preventDefault()
		this.props.onSelect(this.props.answerData)
	}
}

	handleChange = () => {
		// Process mouse clicks only
		this.props.onSelect(this.props.answerData)
	}


	buildAriaLabel = (): string => {
		const { answerData, accessibility } = this.props
		if (answerData.media.type === AnswerType.TEXT) {
			// Prefer MathJax's speech ("x squared" rather than the glyphs), but never wait for it:
			// ensurePlainText gives a correct label on the first render and for math-free answers.
			// Reading payload.text directly would announce the rich-text markup out loud.
			return this.state.textSpeech || ensurePlainText(answerData.media.payload.text)
		}

		// For image answers, use accessibility data if available
		const accessibilityData = accessibility?.[answerData.uid]
		if (accessibilityData?.altText) {
			return accessibilityData.altText
		}

		// TODO: Add default alt text for image answers
		return 'Answer image'
	}

	buildAnswerColorStyle = (answerState: string, answerSkin: IAnswerSkin, delay: number, answerType: AnswerType) => {
		let style: React.CSSProperties = {}
		if (answerState === answerStates.UNANSWERED) {
			style = {
				borderColor: answerSkin.primary,
				backgroundColor: answerType === AnswerType.IMAGE ? palette.white : answerSkin.secondary,
			}
		} else if (answerState === answerStates.SELECTED) {
			style = {
				borderColor: answerSkin.primary,
				backgroundColor: answerType === AnswerType.IMAGE ? palette.white : answerSkin.secondary,
				borderWidth: '4px',
			}
		} else {
			style = {
				transitionDelay: `${delay}ms`,
			}
		}

		return style
	}

	buildAnswerMedia(answerMedia: IGameAnswerMediaText | IGameAnswerMediaImage, answerState: string) {
		if (answerMedia.type === AnswerType.IMAGE) {
			return (
				<AnswerImage
					src={answerMedia.payload.source}
					alt=""
					aria-hidden="true"
					style={{
						opacity: answerState === answerStates.UNSELECTED ? 0.5 : 1,
					}}
				/>
			)
		} else if (answerMedia.type === AnswerType.TEXT) {
			// aria-hidden: the accessible name comes from the hidden input's aria-label, which is fed
			// by handleTextSpeechChange. Typeset glyphs must never reach the screen reader.
			return (
				<RichTextContent
					as={AnswerText}
					aria-hidden="true"
					content={answerMedia.payload.text}
					onSpeechChange={this.handleTextSpeechChange}
					// The answer tile is a <label> for a hidden input: a click or tab stop landing on the
					// formula would not select the answer. The input's aria-label carries the speech.
					isInteractive={false}
				/>
			)
		}
	}

	render() {
		const { answerState, answerData, answerSkin, headerText, animationPath, delay } = this.props

		const answerMedia = answerData.media
		const answerType = answerMedia.type

		const style = this.buildAnswerColorStyle(answerState, answerSkin, delay, answerType)

		const segmentInit = -20 - (delay * 24) / 1000

		return (
			<Wrapper
				className={answerState}
				style={{
					transitionDelay: `${delay}ms`,
				}}
			>
				<VisuallyHiddenInput
					type={this.props.isMultipleAnswer ? 'checkbox' : 'radio'}
					id={`answer-${answerData.uid}`}
					name={this.props.isMultipleAnswer ? `question-${answerData.uid}` : 'question-radio-group'}
					checked={answerState === answerStates.SELECTED}
					onChange={this.handleChange}
					onKeyDown={this.handleKeyDown}
					disabled={answerState === answerStates.CORRECT || answerState === answerStates.INCORRECT}
					aria-label={this.buildAriaLabel()}
				/>
				<AnswerColor style={style} className={answerState} />
				<AnswerMediaContainer>
					{this.buildAnswerMedia(answerMedia, answerState)}
				</AnswerMediaContainer>
				{(answerState === answerStates.CORRECT || answerState === answerStates.INCORRECT) && (
					<AnswerHeaderContainer
						style={{
							transitionDelay: `${delay}ms`,
							animationDelay: `${delay}ms`,
						}}
						className={answerState}
					>
						<AnswerHeaderText>{headerText}</AnswerHeaderText>
						<AnswerAnimation>
							<Asset
								asset={{
									path: animationPath,
									type: assetTypes.ANIMATION as AssetType,
									config: { segmentsData: { segments: [segmentInit, 100], force: true } },
								}}
							/>
						</AnswerAnimation>
					</AnswerHeaderContainer>
				)}
				{!this.props.isMultipleAnswer && answerState === answerStates.SELECTED && (
					<AnswerCheckmark answerSkin={answerSkin}>
						<AnswerCheckmarkIcon src={checkIcon} alt="" />
					</AnswerCheckmark>
				)}
				{this.props.isMultipleAnswer &&
					(answerState === answerStates.SELECTED || answerState === answerStates.UNANSWERED) && (
						<AnswerCheckmark
							isMultipleAnswer={this.props.isMultipleAnswer}
							answerState={answerState}
							answerSkin={answerSkin}
						>
							{answerState === answerStates.SELECTED && <AnswerCheckmarkIcon src={checkIcon} alt="" />}
						</AnswerCheckmark>
					)}
			</Wrapper>
		)
	}
}

export default Answer
